/**
 * What to show when the progress load fails.
 *
 * `ProgressContext` keeps a localStorage copy of progress for a signed-OUT
 * visitor, under one key with no account in it (`sheets-training-progress`).
 * That is fine for the visitor who wrote it. It is not fine as a fallback for a
 * signed-in trainee whose read failed: on a shared machine — a training room, a
 * handed-over laptop, exactly what `localKeys.js` exists for — it hands one
 * person another's completions, and since completion is now read from this map
 * (`docs/completion-truth.md` C1), those completions are shown as theirs.
 *
 * So a signed-in account gets nothing rather than somebody else's record. The
 * lesson then renders as un-started, which is honest: the read failed, so
 * nobody knows what they have finished. Their real progress is on the server and
 * comes back on the next successful load; writes use `merge`, so nothing is
 * overwritten in the meantime.
 */

/**
 * The progress map to fall back to.
 *
 * `uid` is the signed-in account, or null/undefined when signed out.
 * `anonymous` is whatever the shared localStorage key holds.
 */
export function fallbackProgress({ uid = null, anonymous = null } = {}) {
  // Signed in: never the shared key. An empty map, always.
  if (uid) return {}
  // Signed out: the shared key IS this visitor's own store.
  return anonymous && typeof anonymous === 'object' && !Array.isArray(anonymous)
    ? anonymous
    : {}
}
